import {Link as RouterLink} from 'react-router-dom'
import {Trans, useTranslation} from 'react-i18next'
import ContactUsButton from '../components/ContactUsButton'
import './ImprintPage.css'

interface TocEntry {
  readonly id: string
  readonly key: string
}

const TOC: readonly TocEntry[] = [
  {id: 'company', key: 'company'},
  {id: 'reps', key: 'reps'},
  {id: 'contact', key: 'contact'},
  {id: 'supervision', key: 'supervision'},
  {id: 'odr', key: 'odr'},
]

const PROSE_COMPONENTS = {
  refund: <RouterLink to="/refund" />,
  privacy: <RouterLink to="/privacy" />,
  contact: <RouterLink to="/contact" />,
  dpa: <a href="https://dataprotection.gov.sk" target="_blank" rel="noreferrer" />,
  soi: <a href="https://www.soi.sk" target="_blank" rel="noreferrer" />,
  odr: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer" />,
  strong: <strong />,
  em: <em />,
}

const pad2 = (n: number) => String(n).padStart(2, '0')

const ImprintPage = () => {
  const {t} = useTranslation()

  return (
    <div className="imprint-root">
      <div className="im-grid" aria-hidden />

      <main className="imprint-page">
        <header className="im-hero">
          <span className="im-eyebrow">
            <span className="im-eyebrow-dot" />
            {t('imprint.eyebrow')}
          </span>
          <h1 className="im-title">
            {t('imprint.titleLead')} <em>{t('imprint.titleEm')}</em>
          </h1>
          <p className="im-lede">{t('imprint.lede')}</p>
          <div className="im-meta">
            <span>{t('imprint.metaUpdated')}</span>
            <span className="im-meta-sep" />
            <span>{t('imprint.metaGoverned')}</span>
          </div>
        </header>

        <div className="im-layout">
          <aside className="im-toc" aria-label={t('imprint.tocLabel')}>
            <div className="im-toc-label">{t('imprint.tocLabel')}</div>
            {TOC.map((entry, i) => (
              <a key={entry.id} href={`#${entry.id}`}>
                <span className="im-toc-num">{pad2(i + 1)}</span>
                <span>{t(`imprint.toc.${entry.key}`)}</span>
              </a>
            ))}
          </aside>

          <article className="im-prose">
            <section id="company">
              <h2 data-num="01">{t('imprint.sections.company.title')}</h2>
              <div className="im-address">
                <Trans
                  i18nKey="imprint.sections.company.addressBody"
                  components={{br: <br />, strong: <strong />, em: <em />}}
                />
              </div>
              <p>
                <Trans i18nKey="imprint.sections.company.registration" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="reps">
              <h2 data-num="02">{t('imprint.sections.reps.title')}</h2>
              <p>
                <Trans i18nKey="imprint.sections.reps.list" components={PROSE_COMPONENTS} />
              </p>
              <p>{t('imprint.sections.reps.authority')}</p>
            </section>

            <section id="contact">
              <h2 data-num="03">{t('imprint.sections.contact.title')}</h2>
              <p>
                <Trans i18nKey="imprint.sections.contact.formLine" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="imprint.sections.contact.emailLine" components={PROSE_COMPONENTS} />
              </p>
              <p>{t('imprint.sections.contact.note')}</p>
            </section>

            <section id="supervision">
              <h2 data-num="04">{t('imprint.sections.supervision.title')}</h2>
              <p>
                <Trans i18nKey="imprint.sections.supervision.trade" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="imprint.sections.supervision.dataProtection" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="odr">
              <h2 data-num="05">{t('imprint.sections.odr.title')}</h2>
              <p>
                <Trans i18nKey="imprint.sections.odr.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="imprint.sections.odr.p2" components={PROSE_COMPONENTS} />
              </p>
              <div className="im-contact-cta">
                <ContactUsButton />
              </div>
            </section>
          </article>
        </div>
      </main>
    </div>
  )
}

export default ImprintPage
